import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { onUpdateAvailable, applyUpdate } from '@/lib/serviceWorker';
import { useI18n } from '@/lib/i18n';

export function UpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Usar getState para evitar problemas de hooks em contextos onde React não está pronto
  const getT = useCallback(() => {
    try {
      return useI18n.getState().t;
    } catch {
      return (key: string) => key; // fallback
    }
  }, []);
  
  const t = getT();
  
  useEffect(() => {
    const unsubscribe = onUpdateAvailable(() => {
      setShowUpdate(true);
    });
    
    return unsubscribe;
  }, []);
  
  const handleUpdate = () => {
    setIsUpdating(true);
    applyUpdate();
    
    // Fallback: se não recarregar em 3s, força manualmente
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };
  
  const handleDismiss = () => {
    setShowUpdate(false);
  };
  
  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 z-[9999] pointer-events-none flex justify-center"
        >
          <div className="pointer-events-auto bg-gradient-to-r from-cyan-900/95 to-cyan-800/95 rk-blur rounded-xl border border-cyan-500/50 shadow-2xl shadow-cyan-500/20 p-4 max-w-md w-full">
            <div className="flex items-start gap-3">
              {/* Ícone */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              
              {/* Conteúdo */}
              <div className="flex-1">
                <h3 className="text-white font-semibold text-sm">
                  {t("ui.newVersionAvailable")}
                </h3>
                <p className="text-cyan-200/80 text-xs mt-1">
                  {t("ui.updateReady")}
                </p>
                
                {/* Botões */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleUpdate}
                    disabled={isUpdating}
                    className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-cyan-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isUpdating ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t("ui.updating")}
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t("ui.updateNow")}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDismiss}
                    disabled={isUpdating}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white/80 text-xs font-medium rounded-lg transition-colors"
                  >
                    {t("ui.later")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
